import { JsonFormat, JsonFormatError } from "@redotech/json/format";
import { Json } from "@redotech/json/json";
import { privacy } from "@redotech/schema-privacy";
import { isDef } from "@redotech/util/checks";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const iso8601Regex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;
const iso8601DateString = z
  .string()
  .refine((value) => iso8601Regex.test(value), {
    message: "Invalid ISO 8601 date format",
  });

const dateOrStringOrNumberAsIso8601DateString = z
  .union([z.date(), z.string(), z.number(), z.null(), z.undefined()])
  .transform((value) => {
    if (typeof value === "string") {
      return iso8601Regex.test(value) ? value : new Date(value).toISOString();
    } else if (value instanceof Date) {
      return value.toISOString();
    } else if (value == null) {
      return new Date().toISOString();
    } else {
      return new Date(value).toISOString();
    }
  })
  .refine((value) => iso8601Regex.test(value), {
    message: "Invalid ISO 8601 date format",
  })
  .default(() => new Date().toISOString());

const stringOrNumberAsString = z.union([
  z.string(),
  z.number().transform(String),
]);

const numberOrStringAsNumber = z
  .union([z.number(), z.string()])
  .transform((value) => (typeof value === "string" ? +value : value));

const urlSchema = z.string().url();

const MoneySetSchema_ = z
  .object({
    amount: stringOrNumberAsString.meta({ privacy: privacy.money }),
    currency_code: z.string().meta({ privacy: privacy.money }),
  })
  .meta({ privacy: privacy.structured });
export const MoneySetSchema: z.ZodType<MoneySet> = MoneySetSchema_;
export interface MoneySet extends z.infer<typeof MoneySetSchema_> {}

const OptionalMoneySetSchema = z
  .object({
    shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
    presentment_money: MoneySetSchema.meta({ privacy: privacy.structured }),
  })
  .nullish()
  .meta({ privacy: privacy.structured });

const OptionalMoneySetOrStringOrArrayAsOptionalMoneySet = z
  .union([
    z.string().meta({ privacy: privacy.untypedPayload }),
    OptionalMoneySetSchema,
    z.array(z.any().meta({ privacy: privacy.untypedPayload })),
  ])
  .meta({ privacy: privacy.structured })
  .transform((value) => {
    // CommentSold orders just seem to send whatever they want here-- usually a string with a "0" or an empty array.
    if (typeof value === "string") {
      return undefined;
    } else if (Array.isArray(value)) {
      return undefined;
    } else {
      return value;
    }
  });

const AmountTotalOrArrayAsString = z
  .union([z.string(), z.array(z.any())])
  .meta({ privacy: privacy.money })
  .transform((value) => {
    if (typeof value === "string") {
      return value;
    } else {
      return "0";
    }
  });

const CustomerAddressSchema_ = z
  .object({
    address1: z
      .string()
      .nullish()
      .meta({ privacy: privacy.address_line.customer }),
    address2: z
      .string()
      .nullish()
      .meta({ privacy: privacy.address_line.customer }),
    city: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    company: z
      .string()
      .nullish()
      .meta({ privacy: privacy.company_name.customer }),
    country: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    first_name: z
      .string()
      .nullish()
      .meta({ privacy: privacy.person_name.customer }),
    last_name: z
      .string()
      .nullish()
      .meta({ privacy: privacy.person_name.customer }),
    phone: z.string().nullish().meta({ privacy: privacy.phone.customer }),
    province: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    zip: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    name: z.string().nullish().meta({ privacy: privacy.person_name.customer }),
    province_code: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    country_code: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    latitude: z.number().nullish().meta({ privacy: privacy.geo.coordinate }),
    longitude: z.number().nullish().meta({ privacy: privacy.geo.coordinate }),
  })
  .meta({ privacy: privacy.structured });
const CustomerAddressSchema: z.ZodType<CustomerAddress> =
  CustomerAddressSchema_;
export interface CustomerAddress extends z.infer<
  typeof CustomerAddressSchema_
> {}

const ClientDetailsSchema_ = z
  .object({
    accept_language: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    browser_height: z.number().nullish().meta({ privacy: privacy.dimension }),
    browser_ip: z
      .string()
      .nullish()
      .meta({ privacy: privacy.ipAddress.customer }),
    browser_width: z.number().nullish().meta({ privacy: privacy.dimension }),
    session_hash: z.string().nullish().meta({ privacy: privacy.id.session }),
    user_agent: z
      .string()
      .nullish()
      .meta({ privacy: privacy.userAgent.customer }),
  })
  .meta({ privacy: privacy.structured });
const ClientDetailsSchema: z.ZodType<ClientDetails> = ClientDetailsSchema_;
export interface ClientDetails extends z.infer<typeof ClientDetailsSchema_> {}

const DiscountApplicationSchemaAllocationMethodEnum = createEnum(
  ["across", "each", "one"],
  { enumName: "DiscountApplicationAllocationMethod" },
);
const DiscountApplicationSchemaTargetSelectionEnum = createEnum(
  ["all", "entitled", "explicit"],
  { enumName: "DiscountApplicationTargetSelection" },
);
const DiscountApplicationSchemaTargetTypeEnum = createEnum(
  ["line_item", "shipping_line"],
  { enumName: "DiscountApplicationTargetType" },
);
const DiscountApplicationSchemaTypeEnum = createEnum(
  ["automatic", "manual", "script", "discount_code"],
  { enumName: "DiscountApplicationType" },
);
const DiscountApplicationValueTypeEnum = createEnum(
  ["fixed_amount", "percentage"],
  { enumName: "DiscountApplicationValueType" },
);

const DiscountApplicationSchema_ = z
  .object({
    allocation_method: DiscountApplicationSchemaAllocationMethodEnum.default(
      "across",
    ).meta({ privacy: privacy.enumOrStatus }),
    code: z.string().nullish().meta({ privacy: privacy.id.capability }),
    description: z
      .string()
      .nullish()
      .meta({ privacy: privacy.productCatalogText }),
    target_selection: DiscountApplicationSchemaTargetSelectionEnum.default(
      "explicit",
    ).meta({ privacy: privacy.enumOrStatus }),
    target_type: DiscountApplicationSchemaTargetTypeEnum.default(
      "line_item",
    ).meta({ privacy: privacy.enumOrStatus }),
    title: z.string().nullish().meta({ privacy: privacy.productCatalogText }),
    type: DiscountApplicationSchemaTypeEnum.default("manual").meta({
      privacy: privacy.enumOrStatus,
    }),
    value_type: DiscountApplicationValueTypeEnum.default("fixed_amount").meta({
      privacy: privacy.enumOrStatus,
    }),
    value: z.string().meta({ privacy: privacy.money }),
  })
  .meta({ privacy: privacy.structured });
const DiscountApplicationSchema: z.ZodType<DiscountApplication> =
  DiscountApplicationSchema_;
export interface DiscountApplication extends z.infer<
  typeof DiscountApplicationSchema_
> {}

const DiscountCodeValueTypeEnum = createEnum(
  ["fixed_amount", "percentage", "shipping"],
  { enumName: "DiscountCodeValueType" },
);
const DiscountCodeSchema_ = z
  .object({
    amount: z.string().meta({ privacy: privacy.money }),
    code: z.string().meta({ privacy: privacy.id.capability }),
    type: DiscountCodeValueTypeEnum.meta({ privacy: privacy.enumOrStatus }),
  })
  .meta({ privacy: privacy.structured });
const DiscountCodeSchema: z.ZodType<DiscountCode> = DiscountCodeSchema_;
export interface DiscountCode extends z.infer<typeof DiscountCodeSchema_> {}

const TaxLineSchema_ = z
  .object({
    title: z.string().nullish().meta({ privacy: privacy.productCatalogText }),
    price: z.string().meta({ privacy: privacy.money }),
    rate: z.number().nullish().meta({ privacy: privacy.money }),
    price_set: OptionalMoneySetSchema.meta({ privacy: privacy.structured }),
    channel_liable: z.boolean().nullish().meta({ privacy: privacy.flag }),
  })
  .meta({ privacy: privacy.structured });
export const TaxLineSchema: z.ZodType<TaxLine> = TaxLineSchema_;
export interface TaxLine extends z.infer<typeof TaxLineSchema_> {}

const DiscountAllocationSchema_ = z
  .object({
    amount: z.string().meta({ privacy: privacy.money }),
    discount_application_index: z.number().meta({ privacy: privacy.quantity }),
    amount_set: OptionalMoneySetSchema.meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
const DiscountAllocationSchema: z.ZodType<DiscountAllocation> =
  DiscountAllocationSchema_;
export interface DiscountAllocation extends z.infer<
  typeof DiscountAllocationSchema_
> {}

const CompleteOrderFulfillmentStatusZodSchema = z
  .object({
    _value: z.string().meta({ privacy: privacy.nonPii }),
    _dbValue: z.string().nullable().meta({ privacy: privacy.nonPii }),
    _displayValue: z.string().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

/**
 * In OrderFulfillmentStatusEnum, unfulfilled is represented as null. This
 * CompleteOrderFulfillmentStatus enum is for convenience.
 */
export class CompleteOrderFulfillmentStatus {
  static readonly FULFILLED = new CompleteOrderFulfillmentStatus(
    "fulfilled",
    "fulfilled",
    "Fulfilled",
  );
  static readonly RESTOCKED = new CompleteOrderFulfillmentStatus(
    "restocked",
    "restocked",
    "Restocked",
  );
  static readonly PARTIAL = new CompleteOrderFulfillmentStatus(
    "partial",
    "partial",
    "Partial",
  );
  static readonly NOT_ELIGIBLE = new CompleteOrderFulfillmentStatus(
    "not_eligible",
    "not_eligible",
    "Not eligible",
  );
  static readonly UNFULFILLED = new CompleteOrderFulfillmentStatus(
    "unfulfilled",
    null,
    "Unfulfilled",
  );
  static readonly CANCELED = new CompleteOrderFulfillmentStatus(
    "canceled",
    "canceled",
    "Canceled",
  );

  static readonly allValues = [
    CompleteOrderFulfillmentStatus.FULFILLED.value,
    CompleteOrderFulfillmentStatus.RESTOCKED.value,
    CompleteOrderFulfillmentStatus.PARTIAL.value,
    CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.value,
    CompleteOrderFulfillmentStatus.UNFULFILLED.value,
    CompleteOrderFulfillmentStatus.CANCELED.value,
  ];

  static readonly allDbValues = [
    CompleteOrderFulfillmentStatus.FULFILLED.dbValue,
    CompleteOrderFulfillmentStatus.RESTOCKED.dbValue,
    CompleteOrderFulfillmentStatus.PARTIAL.dbValue,
    CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.dbValue,
    CompleteOrderFulfillmentStatus.UNFULFILLED.dbValue,
    CompleteOrderFulfillmentStatus.CANCELED.dbValue,
  ];

  static fromValue(value: string): CompleteOrderFulfillmentStatus {
    switch (value) {
      case CompleteOrderFulfillmentStatus.FULFILLED.value:
        return CompleteOrderFulfillmentStatus.FULFILLED;
      case CompleteOrderFulfillmentStatus.RESTOCKED.value:
        return CompleteOrderFulfillmentStatus.RESTOCKED;
      case CompleteOrderFulfillmentStatus.PARTIAL.value:
        return CompleteOrderFulfillmentStatus.PARTIAL;
      case CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.value:
        return CompleteOrderFulfillmentStatus.NOT_ELIGIBLE;
      case CompleteOrderFulfillmentStatus.UNFULFILLED.value:
        return CompleteOrderFulfillmentStatus.UNFULFILLED;
      case CompleteOrderFulfillmentStatus.CANCELED.value:
        return CompleteOrderFulfillmentStatus.CANCELED;
      default:
        throw new Error(
          `Invalid CompleteOrderFulfillmentStatus value ${value}`,
        );
    }
  }

  static fromDbValue(
    dbValue: string | null | undefined,
  ): CompleteOrderFulfillmentStatus | undefined {
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
    switch (dbValue) {
      case CompleteOrderFulfillmentStatus.FULFILLED.dbValue:
        return CompleteOrderFulfillmentStatus.FULFILLED;
      case CompleteOrderFulfillmentStatus.RESTOCKED.dbValue:
        return CompleteOrderFulfillmentStatus.RESTOCKED;
      case CompleteOrderFulfillmentStatus.PARTIAL.dbValue:
        return CompleteOrderFulfillmentStatus.PARTIAL;
      case CompleteOrderFulfillmentStatus.NOT_ELIGIBLE.dbValue:
        return CompleteOrderFulfillmentStatus.NOT_ELIGIBLE;
      case CompleteOrderFulfillmentStatus.UNFULFILLED.dbValue:
        return CompleteOrderFulfillmentStatus.UNFULFILLED;
      case CompleteOrderFulfillmentStatus.CANCELED.dbValue:
        return CompleteOrderFulfillmentStatus.CANCELED;
      default:
        return undefined;
    }
  }

  static jsonFormat: JsonFormat<CompleteOrderFulfillmentStatus> = {
    read(json: Json): CompleteOrderFulfillmentStatus {
      if (
        typeof json === "string" &&
        CompleteOrderFulfillmentStatus.allValues.includes(json)
      ) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return CompleteOrderFulfillmentStatus.fromValue(json)!;
      } else {
        throw new JsonFormatError(
          `Invalid CompleteOrderFulfillmentStatus ${json}`,
          [],
        );
      }
    },
    write(orderFulfillmentStatus: CompleteOrderFulfillmentStatus): Json {
      return orderFulfillmentStatus.value;
    },
  };

  /**
   * Attempts to parse as an object first and then as a nullable string so the
   * RPC layer doesn't have to have a different schema than the db layer.
   */
  static zodParser = CompleteOrderFulfillmentStatusZodSchema.transform(
    (obj) => {
      return CompleteOrderFulfillmentStatus.fromValue(obj._value);
    },
  ).or(
    z
      .string()
      .nullable()
      .transform(
        (dbValue: string | null) =>
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          CompleteOrderFulfillmentStatus.fromDbValue(dbValue)!,
      )
      .refine((value) => isDef(value), {
        message: "Unable to parse CompleteOrderFulfillmentStatus from string",
      })
      .meta({ privacy: privacy.structured }),
  );

  static fromShopifyOrder(
    shopifyOrder: Pick<ShopifyOrder, "cancelled_at" | "fulfillment_status">,
  ): CompleteOrderFulfillmentStatus | undefined {
    if (shopifyOrder.cancelled_at) {
      return CompleteOrderFulfillmentStatus.CANCELED;
    }
    // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME

    switch (shopifyOrder.fulfillment_status) {
      case null:
        return CompleteOrderFulfillmentStatus.UNFULFILLED;
      case "fulfilled":
        return CompleteOrderFulfillmentStatus.FULFILLED;
      case "restocked":
        return CompleteOrderFulfillmentStatus.RESTOCKED;
      case "partial":
        return CompleteOrderFulfillmentStatus.PARTIAL;
      case "not_eligible":
        return CompleteOrderFulfillmentStatus.NOT_ELIGIBLE;
      default:
        return undefined;
    }
  }

  private constructor(
    private readonly _value: string,
    private readonly _dbValue: string | null,
    private readonly _displayValue: string,
  ) {}

  get value(): string {
    return this._value;
  }

  get dbValue(): string | null {
    return this._dbValue;
  }

  get displayValue(): string {
    return this._displayValue;
  }
}

export const OrderFulfillmentStatusEnum = createEnum(
  ["fulfilled", "unfulfilled", "restocked", "partial", "not_eligible"],
  { enumName: "OrderFulfillmentStatus" },
).describe(`- fulfilled: All items in the order have been shipped
- unfulfilled: No items in the order have been shipped
- restocked: Order has been cancelled and all items within that order have been returned to inventory
- partial: Some items in the order have been shipped, but not all
- not_eligible: Items have been fulfilled and do not require shipping
`);
// NOTE: The above describe is used by LLMs to understand the available order fulfillment statuses, please update this if you change the enum and
// use clear natural language to describe the statuses.

export type OrderFulfillmentStatus = z.infer<typeof OrderFulfillmentStatusEnum>;

const PropertySchema = z
  .object({
    name: z.string().nullish().meta({ privacy: privacy.productCatalogText }),
    value: z.any().nullish().meta({ privacy: privacy.untypedPayload }),
  })
  .meta({ privacy: privacy.structured });

const LineItemImageSchema_ = z
  .object({
    id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    product_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    position: z.number().nullish().meta({ privacy: privacy.quantity }),
    created_at: dateOrStringOrNumberAsIso8601DateString.meta({
      privacy: privacy.timestamp,
    }),
    updated_at: dateOrStringOrNumberAsIso8601DateString.meta({
      privacy: privacy.timestamp,
    }),
    alt: z.string().nullish().meta({ privacy: privacy.productCatalogText }),
    width: z.number().nullish().meta({ privacy: privacy.dimension }),
    height: z.number().nullish().meta({ privacy: privacy.dimension }),
    src: z.string().nullish().default("").meta({ privacy: privacy.publicUrl }),
    variant_ids: z
      .array(z.union([z.number(), z.string()]))
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    admin_graphql_api_id: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
  })
  .meta({ privacy: privacy.structured });

export const LineItemSchema_ = z
  .object({
    id: z
      .union([z.number(), z.string()])
      .meta({ privacy: privacy.id.external.resource }),
    current_quantity: z.number().nullish().meta({ privacy: privacy.quantity }),
    tip_payment_gateway: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    tip_payment_method: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    image: LineItemImageSchema_.nullish().meta({ privacy: privacy.structured }),
    /**
     * Not a Shopify field. Carries the extra images the custom-integration order
     * sync accepts, which Shopify's single `image` has nowhere to put.
     */
    additional_images: z
      .array(LineItemImageSchema_)
      .nullish()
      .meta({ privacy: privacy.structured }),
    /**
     * This will be true if the item is a redo returns coverage item and it was
     * purchased from a valid location, or if the orders are covered by the merchant.
     */
    protected: z
      .union([z.boolean(), z.array(z.string())])
      .nullish()
      .meta({ privacy: privacy.untypedPayload }), // sometimes this is a boolean, others it is an array like ["re:do"] idk why
    /**
     * This will be true if the item is a redo final sale returns coverage item and
     * it was purchased from a valid location.
     *
     * @deprecated For use of determining if the item is covered by final sale. Use Order.finalSaleLineItemsProtection instead.
     */
    finalSaleProtected: z.boolean().nullish().meta({ privacy: privacy.flag }),
    tags: z
      .array(z.string())
      .nullish()
      .meta({ privacy: privacy.resourceLabel }),
    tip: z
      .object({
        payment_method: z
          .string()
          .nullish()
          .meta({ privacy: privacy.enumOrStatus }),
        payment_gateway: z
          .string()
          .nullish()
          .meta({ privacy: privacy.enumOrStatus }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    attributed_staffs: z
      .array(
        z
          .object({
            id: z.string().meta({ privacy: privacy.id.external.resource }),
            quantity: z.number().meta({ privacy: privacy.quantity }),
          })
          .meta({ privacy: privacy.structured }),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
    admin_graphql_api_id: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    variant_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    title: z.string().default("").meta({ privacy: privacy.productCatalogText }),
    quantity: z.number().default(1).meta({ privacy: privacy.quantity }),
    price: z.string().default("0").meta({ privacy: privacy.money }),
    price_set: OptionalMoneySetSchema.meta({ privacy: privacy.structured }),
    pre_tax_price: z.string().nullish().meta({ privacy: privacy.money }),
    pre_tax_price_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    grams: z.number().nullish().meta({ privacy: privacy.dimension }),
    sku: z.string().nullish().meta({ privacy: privacy.productSku }),
    vendor: z
      .string()
      .nullish()
      .meta({ privacy: privacy.company_name.merchant }),
    variant_title: z
      .string()
      .nullish()
      .meta({ privacy: privacy.productCatalogText }),
    fulfillment_service: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    product_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    additional_product_id: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    requires_shipping: z.boolean().nullish().meta({ privacy: privacy.flag }),
    taxable: z.boolean().nullish().meta({ privacy: privacy.flag }),
    gift_card: z.boolean().nullish().meta({ privacy: privacy.flag }),
    name: z.string().default("").meta({ privacy: privacy.productCatalogText }),
    variant_inventory_management: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    properties: z
      .array(PropertySchema)
      .default([])
      .meta({ privacy: privacy.structured }),
    product_exists: z.boolean().nullish().meta({ privacy: privacy.flag }),
    fulfillable_quantity: z
      .number()
      .default(1)
      .meta({ privacy: privacy.quantity }),
    returnable_quantity: z
      .number()
      .nullish()
      .meta({ privacy: privacy.quantity }),
    total_discount: stringOrNumberAsString
      .default("0")
      .meta({ privacy: privacy.money }),
    fulfillment_status: OrderFulfillmentStatusEnum.nullish().meta({
      privacy: privacy.enumOrStatus,
    }),
    total_discount_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    destination_location: z
      .object({
        id: z
          .union([z.number(), z.string()])
          .meta({ privacy: privacy.id.external.resource }),
        country_code: z
          .string()
          .nullish()
          .meta({ privacy: privacy.geo.coarse }),
        province_code: z
          .string()
          .nullish()
          .meta({ privacy: privacy.geo.coarse }),
        name: z
          .string()
          .nullish()
          .meta({ privacy: privacy.person_name.customer }),
        address1: z
          .string()
          .nullish()
          .meta({ privacy: privacy.address_line.customer }),
        address2: z
          .string()
          .nullish()
          .meta({ privacy: privacy.address_line.customer }),
        city: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
        zip: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    origin_location: z
      .object({
        id: z
          .union([z.number(), z.string()])
          .meta({ privacy: privacy.id.external.resource }),
        country_code: z.string().meta({ privacy: privacy.geo.coarse }),
        province_code: z.string().meta({ privacy: privacy.geo.coarse }),
        name: z.string().meta({ privacy: privacy.resourceLabel }),
        address1: z.string().meta({ privacy: privacy.address_line.merchant }),
        address2: z
          .string()
          .nullish()
          .meta({ privacy: privacy.address_line.merchant }),
        city: z.string().meta({ privacy: privacy.geo.coarse }),
        zip: z.string().meta({ privacy: privacy.geo.coarse }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    discount_allocations: z
      .array(DiscountAllocationSchema)
      .default([])
      .meta({ privacy: privacy.structured }),
    fulfillment_line_item_id: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    tax_code: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
    tax_lines: z
      .array(TaxLineSchema)
      .default([])
      .meta({ privacy: privacy.structured }),
    duties: z
      .array(
        z
          .object({
            id: z
              .union([z.number(), z.string()])
              .meta({ privacy: privacy.id.external.resource }),
            harmonized_system_code: z
              .string()
              .nullish()
              .meta({ privacy: privacy.nonPii }),
            country_code_of_origin: z
              .string()
              .nullish()
              .meta({ privacy: privacy.geo.coarse }),
            price_set: OptionalMoneySetSchema.meta({
              privacy: privacy.structured,
            }),
            tax_lines: z
              .array(TaxLineSchema)
              .meta({ privacy: privacy.structured }),
            admin_graphql_api_id: z
              .string()
              .meta({ privacy: privacy.id.external.resource }),
          })
          .meta({ privacy: privacy.structured }),
      )
      .default([]),
    discount_price: z
      .string()
      .nullish()
      .optional()
      .meta({ privacy: privacy.money }),
    reason: z
      .string()
      .nullish()
      .optional()
      .meta({ privacy: privacy.enumOrStatus }),
    loyaltyAmount: numberOrStringAsNumber
      .nullish()
      .meta({ privacy: privacy.money }),
    delivery_date: z.string().nullish().meta({ privacy: privacy.timestamp }),
    duties_included: z
      .any()
      .optional()
      .meta({ privacy: privacy.untypedPayload }),
    merchant_business_entity_id: z
      .any()
      .optional()
      .meta({ privacy: privacy.untypedPayload }),
    total_cash_rounding_payment_adjustment_set: z
      .any()
      .optional()
      .meta({ privacy: privacy.untypedPayload }),
    total_cash_rounding_refund_adjustment_set: z
      .any()
      .optional()
      .meta({ privacy: privacy.untypedPayload }),
    redoEligible: z.boolean().nullish().meta({ privacy: privacy.flag }), // Not from Shopify, added by redo
    redoPackageProtectionEligible: z
      .boolean()
      .nullish()
      .meta({ privacy: privacy.flag }), // Not from Shopify, added by redo
    productMetadata: z
      .any()
      .optional()
      .meta({ privacy: privacy.untypedPayload }), // Not from Shopify, added by redo. But specifically for Commerce Cloud GlobalE Orders.
    orderTimeCollections: z
      .array(z.string())
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }), // Not from Shopify, added by redo for BigCommerce
    redoProduct: z.boolean().nullish().meta({ privacy: privacy.flag }), // Not from Shopify, added by redo to mark Redo coverage items
    // Shopify native bundles: shared group id across a bundle's component line
    // items (webhook/REST snake_case shape, added by Shopify 2025-01).
    sales_line_item_group_id: z.coerce
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
  })
  .meta({ privacy: privacy.structured });
export const LineItemSchema: z.ZodType<LineItem> = LineItemSchema_;
export interface LineItem extends z.infer<typeof LineItemSchema_> {}

export const ShipmentStatusEnum = createEnum(
  [
    "label_printed",
    "label_purchased",
    "attempted_delivery",
    "ready_for_pickup",
    "confirmed",
    "in_transit",
    "out_for_delivery",
    "delivered",
    "failure",
    "picked_up",
    "stalled_in_transit",
    "delayed",
    "ready_for_recipient_pickup",
    "carrier_picked_up",
    "returning_to_sender",
    // Shopify started emitting this in 2026; absent from their published list.
    "buyer_action_required",
  ],
  { enumName: "ShipmentStatus" },
);
export type ShipmentStatus = z.infer<typeof ShipmentStatusEnum>;

const FulfillmentStatusEnum = createEnum(
  ["pending", "open", "success", "cancelled", "error", "failure"],
  { enumName: "FulfillmentStatus" },
);

const OriginAddressSchema_ = z
  .object({
    address1: z
      .string()
      .nullish()
      .meta({ privacy: privacy.address_line.merchant }),
    address2: z
      .string()
      .nullish()
      .meta({ privacy: privacy.address_line.merchant }),
    city: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    country_code: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    province_code: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
    zip: z.string().nullish().meta({ privacy: privacy.geo.coarse }),
  })
  .meta({ privacy: privacy.structured });
export const OriginAddressSchema: z.ZodType<OriginAddress> =
  OriginAddressSchema_;
export interface OriginAddress extends z.infer<typeof OriginAddressSchema_> {}

const FulfillmentStatusWithFulfilledFallback = z
  .union([
    FulfillmentStatusEnum,
    z.literal("Fulfilled"),
    z.literal("fulfilled"),
    z.literal("canceled"),
    z.undefined(),
  ])
  .transform((value) => {
    // Need to use both strings to make types narrow correctly.
    // Fallback option mirrors commentsold-order-sync.ts
    if (value === "fulfilled" || value === "Fulfilled" || value === undefined) {
      return "success" as const;
    }
    // Shopify spells this with one l on some payloads; never let it read as success.
    if (value === "canceled") {
      return "cancelled" as const;
    }
    return value;
  });

const ShipmentStatusWithFulfilledFallback = z
  .union([
    ShipmentStatusEnum,
    z.literal("Fulfilled"),
    z.literal("fulfilled"),
    z.undefined(),
  ])
  .transform((value) => {
    // Need to use both strings to make types narrow correctly.
    // Fallback option mirrors commentsold-order-sync.ts
    if (value === "fulfilled" || value === "Fulfilled" || value === undefined) {
      return "in_transit" as const;
    }
    return value;
  });

const FulfillmentSchema_ = z
  .object({
    admin_graphql_api_id: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    created_at: iso8601DateString
      .default(new Date().toISOString())
      .meta({ privacy: privacy.timestamp }),
    id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }), // null for CS orders
    line_items: z.array(LineItemSchema).meta({ privacy: privacy.structured }),
    location_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    location: z
      .object({
        name: z.string().nullish().meta({ privacy: privacy.resourceLabel }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    name: z.string().nullish().meta({ privacy: privacy.resourceLabel }),
    order_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    origin_address: OriginAddressSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    receipt: z.any().optional().meta({ privacy: privacy.untypedPayload }),
    status: FulfillmentStatusWithFulfilledFallback.meta({
      privacy: privacy.enumOrStatus,
    }),
    service: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
    // `.catch` keeps an unrecognized carrier status from failing the whole order
    // parse — shipment status is display data and must never break loading it.
    shipment_status: ShipmentStatusWithFulfilledFallback.nullish()
      .catch(null)
      .meta({ privacy: privacy.enumOrStatus }),
    tracking_company: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    tracking_number: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.tracking }),
    tracking_numbers: z
      .array(z.string().meta({ privacy: privacy.id.tracking }))
      .nullish()
      .meta({ privacy: privacy.id.tracking }),
    tracking_url: z.string().nullish().meta({ privacy: privacy.publicUrl }),
    tracking_urls: z
      .array(z.string().meta({ privacy: privacy.publicUrl }))
      .nullish()
      .meta({ privacy: privacy.publicUrl }),
    updated_at: iso8601DateString
      .nullish()
      .meta({ privacy: privacy.timestamp }),
    variant_inventory_management: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
  })
  .meta({ privacy: privacy.structured });
const FulfillmentSchema: z.ZodType<Fulfillment> = FulfillmentSchema_;
export interface Fulfillment extends z.infer<typeof FulfillmentSchema_> {}

const MarketingConsentSchema_ = z
  .object({
    state: z.string().meta({ privacy: privacy.enumOrStatus }),
    opt_in_level: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
    consent_updated_at: iso8601DateString
      .nullish()
      .meta({ privacy: privacy.timestamp }),
    consent_collected_from: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
  })
  .meta({ privacy: privacy.structured });
const MarketingConsentSchema: z.ZodType<MarketingConsent> =
  MarketingConsentSchema_;
export interface MarketingConsent extends z.infer<
  typeof MarketingConsentSchema_
> {}

const CustomerStateSchema = createEnum(
  ["disabled", "invited", "enabled", "declined"],
  { enumName: "CustomerState" },
);

const CustomerSchema_ = z
  .object({
    accepts_marketing: z.boolean().nullish().meta({ privacy: privacy.flag }),
    accepts_marketing_updated_at: iso8601DateString
      .nullish()
      .meta({ privacy: privacy.timestamp }),
    addresses: z
      .array(CustomerAddressSchema)
      .nullish()
      .meta({ privacy: privacy.structured }),
    admin_graphql_api_id: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    created_at: iso8601DateString
      .nullish()
      .meta({ privacy: privacy.timestamp }),
    currency: z.string().nullish().meta({ privacy: privacy.money }),
    default_address: CustomerAddressSchema_.merge(
      z
        .object({
          default: z.boolean().meta({ privacy: privacy.flag }),
          id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.external.resource }),
          country_name: z
            .string()
            .nullish()
            .meta({ privacy: privacy.geo.coarse }),
          customer_id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.personalExternal }),
        })
        .meta({ privacy: privacy.structured }),
    )
      .nullish()
      .meta({ privacy: privacy.structured }),
    email: z.string().nullish().meta({ privacy: privacy.email.customer }),
    email_marketing_consent: MarketingConsentSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.personalExternal }), // always provided by Shopify, optional in Redo API
    first_name: z
      .string()
      .nullish()
      .meta({ privacy: privacy.person_name.customer }),
    last_name: z
      .string()
      .nullish()
      .meta({ privacy: privacy.person_name.customer }),
    last_order_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    last_order_name: z
      .string()
      .nullish()
      .meta({ privacy: privacy.resourceLabel }),
    metafield: z
      .object({
        key: z.string().meta({ privacy: privacy.resourceLabel }),
        value: z.string().meta({ privacy: privacy.untypedPayload }),
        value_type: z.string().meta({ privacy: privacy.enumOrStatus }),
        namespace: z.string().meta({ privacy: privacy.resourceLabel }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    marketing_opt_in_level: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }), // deprecated, use email_marketing_consent
    multipass_identifier: z
      .string()
      .nullish()
      .meta({ privacy: privacy.credential }),
    note: z.string().nullish().meta({ privacy: privacy.text.merchant }),
    orders_count: z.number().nullish().meta({ privacy: privacy.quantity }),
    password: z.string().nullish().meta({ privacy: privacy.credential }),
    password_confirmation: z
      .string()
      .nullish()
      .meta({ privacy: privacy.credential }),
    phone: z.string().nullish().meta({ privacy: privacy.phone.customer }),
    sms_marketing_consent: MarketingConsentSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    state: CustomerStateSchema.nullish().meta({
      privacy: privacy.enumOrStatus,
    }), // always provided by Shopify, optional in Redo API
    tags: z.string().nullish().meta({ privacy: privacy.resourceLabel }),
    tax_exempt: z.boolean().nullish().meta({ privacy: privacy.flag }),
    tax_exemptions: z
      .array(z.string())
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    total_spent: z.string().nullish().meta({ privacy: privacy.money }),
    updated_at: iso8601DateString
      .nullish()
      .meta({ privacy: privacy.timestamp }),
    verified_email: z.boolean().nullish().meta({ privacy: privacy.flag }), // always provided by Shopify, optional in Redo API
  })
  .meta({ privacy: privacy.structured });
const CustomerSchema: z.ZodType<Customer> = CustomerSchema_;
export interface Customer extends z.infer<typeof CustomerSchema_> {}

const PaymentTermsSchema_ = z
  .object({
    id: z
      .union([z.number(), z.string()])
      .meta({ privacy: privacy.id.external.resource }),
    amount: z.number().nullish().meta({ privacy: privacy.money }),
    currency: z.string().nullish().meta({ privacy: privacy.money }),
    payment_terms_name: z.string().meta({ privacy: privacy.resourceLabel }),
    payment_terms_type: z.string().meta({ privacy: privacy.enumOrStatus }),
    created_at: iso8601DateString.meta({ privacy: privacy.timestamp }),
    updated_at: iso8601DateString.meta({ privacy: privacy.timestamp }),
    due_in_days: z.number().nullish().meta({ privacy: privacy.quantity }),
    payment_schedules: z.array(
      z
        .object({
          id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.external.resource }),
          amount: z.string().meta({ privacy: privacy.money }),
          currency: z.string().meta({ privacy: privacy.money }),
          issued_at: iso8601DateString
            .nullish()
            .meta({ privacy: privacy.timestamp }),
          due_at: iso8601DateString
            .nullish()
            .meta({ privacy: privacy.timestamp }),
          completed_at: iso8601DateString
            .nullish()
            .meta({ privacy: privacy.timestamp }),
          expected_payment_method: z
            .string()
            .nullish()
            .meta({ privacy: privacy.enumOrStatus }),
          created_at: iso8601DateString.meta({ privacy: privacy.timestamp }),
          updated_at: iso8601DateString.meta({ privacy: privacy.timestamp }),
        })
        .meta({ privacy: privacy.structured }),
    ),
  })
  .meta({ privacy: privacy.structured });
const PaymentTermsSchema: z.ZodType<PaymentTerms> = PaymentTermsSchema_;
export interface PaymentTerms extends z.infer<typeof PaymentTermsSchema_> {}

const ShippingLineSchema_ = z
  .object({
    id: z
      .union([z.number(), z.string()])
      .meta({ privacy: privacy.id.external.resource }),
    delivery_category: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    phone: z.string().nullish().meta({ privacy: privacy.phone.customer }),
    discount_allocations: z.array(
      z
        .object({
          amount: z.string().meta({ privacy: privacy.money }),
          discount_application_index: z
            .number()
            .meta({ privacy: privacy.quantity }),
          amount_set: z
            .object({
              shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
              presentment_money: MoneySetSchema.meta({
                privacy: privacy.structured,
              }),
            })
            .meta({ privacy: privacy.structured }),
        })
        .meta({ privacy: privacy.structured }),
    ),
    code: z.string().nullish().meta({ privacy: privacy.id.capability }),
    discounted_price: z.string().meta({ privacy: privacy.money }),
    discounted_price_set: z
      .object({
        shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
        presentment_money: MoneySetSchema.meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
    price: z.string().meta({ privacy: privacy.money }),
    price_set: z
      .object({
        shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
        presentment_money: MoneySetSchema.meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
    source: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
    title: z.string().meta({ privacy: privacy.productCatalogText }),
    tax_lines: z.array(TaxLineSchema).meta({ privacy: privacy.structured }),
    carrier_identifier: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    requested_fulfillment_service_id: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    is_removed: z.boolean().nullish().meta({ privacy: privacy.flag }),
  })
  .meta({ privacy: privacy.structured });
export const ShippingLineSchema: z.ZodType<ShippingLine> = ShippingLineSchema_;
export interface ShippingLine extends z.infer<typeof ShippingLineSchema_> {}

const RefundOrderAdjustmentKindEnum = createEnum(
  ["shipping_refund", "refund_discrepancy"],
  { enumName: "RefundOrderAdjustmentKind" },
);
const RefundDutyTypeEnum = createEnum(["FULL", "PROPORTIONAL"], {
  enumName: "RefundDutyType",
});
const RefundLineItemRestockTypeEnum = createEnum(
  ["no_restock", "cancel", "return", "legacy_restock"],
  { enumName: "RefundLineItemRestockType" },
);
const TransactionErrorCodeEnum = createEnum(
  [
    "incorrect_number",
    "invalid_number",
    "invalid_expiry_date",
    "invalid_cvc",
    "expired_card",
    "incorrect_cvc",
    "incorrect_zip",
    "incorrect_address",
    "card_declined",
    "processing_error",
    "call_issuer",
    "pick_up_card",
    "generic_error",
  ],
  { enumName: "TransactionErrorCode" },
);
const TransactionKindEnum = createEnum(
  ["authorization", "capture", "sale", "void", "refund"],
  { enumName: "TransactionKind" },
);
const TransactionStatusEnum = createEnum(
  ["pending", "error", "success", "failure", "deferred"],
  { enumName: "TransactionStatus" },
);

const PaymentDetailsSchema_ = z
  .object({
    credit_card_bin: z
      .string()
      .nullish()
      .meta({ privacy: privacy.paymentInstrument }),
    avs_result_code: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    cvv_result_code: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    credit_card_number: z.string().meta({ privacy: privacy.cardLast4 }),
    credit_card_company: z.string().meta({ privacy: privacy.enumOrStatus }),
    credit_card_name: z
      .string()
      .nullish()
      .meta({ privacy: privacy.person_name.customer }),
    credit_card_wallet: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    credit_card_expiration_month: z
      .number()
      .nullish()
      .meta({ privacy: privacy.cardExpiry }),
    credit_card_expiration_year: z
      .number()
      .nullish()
      .meta({ privacy: privacy.cardExpiry }),
    buyer_action_info: z
      .object({})
      .nullish()
      .meta({ privacy: privacy.structured }),
    payment_method_name: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
  })
  .meta({ privacy: privacy.structured });
const PaymentDetailsSchema: z.ZodType<PaymentDetails> = PaymentDetailsSchema_;
export interface PaymentDetails extends z.infer<typeof PaymentDetailsSchema_> {}

const TransactionSchema_ = z
  .object({
    admin_graphql_api_id: z
      .string()
      .meta({ privacy: privacy.id.external.resource }),
    amount: z.string().meta({ privacy: privacy.money }),
    authorization: z.string().nullish().meta({ privacy: privacy.paymentToken }),
    authorization_expires_at: iso8601DateString
      .nullish()
      .meta({ privacy: privacy.timestamp }),
    created_at: iso8601DateString.meta({ privacy: privacy.timestamp }),
    currency: z.string().nullish().meta({ privacy: privacy.money }),
    device_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.device }),
    error_code: TransactionErrorCodeEnum.nullish().meta({
      privacy: privacy.enumOrStatus,
    }),
    extended_authorization_attributes: z
      .object({
        standard_authorization_expires_at: iso8601DateString.meta({
          privacy: privacy.timestamp,
        }),
        extended_authorization_expires_at: iso8601DateString
          .nullish()
          .meta({ privacy: privacy.timestamp }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    gateway: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
    id: z
      .union([z.number(), z.string()])
      .meta({ privacy: privacy.id.external.resource }),
    kind: TransactionKindEnum.meta({ privacy: privacy.enumOrStatus }),
    location_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    message: z.string().nullish().meta({ privacy: privacy.text.thirdParty }),
    order_id: z
      .union([z.number(), z.string()])
      .meta({ privacy: privacy.id.external.resource }),
    payment_details: PaymentDetailsSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    parent_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    payment_id: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    payments_refund_attributes: z
      .object({
        status: TransactionStatusEnum.meta({ privacy: privacy.enumOrStatus }),
        acquirer_reference_number: z
          .string()
          .nullish()
          .meta({ privacy: privacy.id.external.resource }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    processed_at: iso8601DateString.meta({ privacy: privacy.timestamp }),
    receipt: z.any().meta({ privacy: privacy.untypedPayload }),
    source_name: z.string().meta({ privacy: privacy.enumOrStatus }),
    status: TransactionStatusEnum.meta({ privacy: privacy.enumOrStatus }),
    total_unsettled_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    test: z.boolean().meta({ privacy: privacy.flag }),
    user_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    currency_exchange_adjustment: z
      .object({
        id: z
          .union([z.number(), z.string()])
          .meta({ privacy: privacy.id.external.resource }),
        adjustment: z.string().meta({ privacy: privacy.money }),
        original_amount: z.string().meta({ privacy: privacy.money }),
        final_amount: z.string().meta({ privacy: privacy.money }),
        currency: z.string().meta({ privacy: privacy.money }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
const TransactionSchema: z.ZodType<Transaction> = TransactionSchema_;
export interface Transaction extends z.infer<typeof TransactionSchema_> {}

const RefundSchema = z
  .object({
    admin_graphql_api_id: z
      .string()
      .meta({ privacy: privacy.id.external.resource }),
    order_id: z
      .union([z.number(), z.string()])
      .meta({ privacy: privacy.id.external.resource }),
    total_duties_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    created_at: iso8601DateString.meta({ privacy: privacy.timestamp }),
    duties: z.array(
      z
        .object({
          duty_id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.external.resource }),
          amount_set: z
            .object({
              shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
              presentment_money: MoneySetSchema.meta({
                privacy: privacy.structured,
              }),
            })
            .meta({ privacy: privacy.structured }),
        })
        .meta({ privacy: privacy.structured }),
    ),
    id: z
      .union([z.number(), z.string()])
      .meta({ privacy: privacy.id.external.resource }),
    note: z.string().nullish().meta({ privacy: privacy.text.merchant }),
    order_adjustments: z.array(
      z
        .object({
          id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.external.resource }),
          order_id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.external.resource }),
          refund_id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.external.resource }),
          amount: z.string().meta({ privacy: privacy.money }),
          tax_amount: z.string().meta({ privacy: privacy.money }),
          reason: z.string().meta({ privacy: privacy.enumOrStatus }),
          kind: RefundOrderAdjustmentKindEnum.meta({
            privacy: privacy.enumOrStatus,
          }),
          amount_set: z
            .object({
              shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
              presentment_money: MoneySetSchema.meta({
                privacy: privacy.structured,
              }),
            })
            .meta({ privacy: privacy.structured }),
          tax_amount_set: z
            .object({
              shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
              presentment_money: MoneySetSchema.meta({
                privacy: privacy.structured,
              }),
            })
            .meta({ privacy: privacy.structured }),
        })
        .meta({ privacy: privacy.structured }),
    ),
    processed_at: iso8601DateString.meta({ privacy: privacy.timestamp }),
    refund_duties: z
      .array(
        z
          .object({
            duty_id: z
              .union([z.number(), z.string()])
              .meta({ privacy: privacy.id.external.resource }),
            refund_type: RefundDutyTypeEnum.meta({
              privacy: privacy.enumOrStatus,
            }),
          })
          .meta({ privacy: privacy.structured }),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
    refund_line_items: z.array(
      z
        .object({
          id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.external.resource }),
          line_item: LineItemSchema.meta({ privacy: privacy.structured }),
          line_item_id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.external.resource }),
          quantity: z.number().meta({ privacy: privacy.quantity }),
          restock_type: RefundLineItemRestockTypeEnum.nullish().meta({
            privacy: privacy.enumOrStatus,
          }),
          location_id: z
            .union([z.number(), z.string()])
            .nullish()
            .meta({ privacy: privacy.id.external.resource }),
          subtotal: z.number().meta({ privacy: privacy.money }),
          total_tax: z.number().meta({ privacy: privacy.money }),
          subtotal_set: z
            .object({
              shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
              presentment_money: MoneySetSchema.meta({
                privacy: privacy.structured,
              }),
            })
            .meta({ privacy: privacy.structured }),
          total_tax_set: z
            .object({
              shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
              presentment_money: MoneySetSchema.meta({
                privacy: privacy.structured,
              }),
            })
            .meta({ privacy: privacy.structured }),
        })
        .meta({ privacy: privacy.structured }),
    ),
    restock: z.boolean().meta({ privacy: privacy.flag }),
    transactions: z
      .array(TransactionSchema)
      .meta({ privacy: privacy.structured }),
    user_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
  })
  .meta({ privacy: privacy.structured });

const SparseRefundSchema = z
  .object({
    refund_line_items: z.array(
      z
        .object({
          line_item_id: z
            .union([z.number(), z.string()])
            .meta({ privacy: privacy.id.external.resource }),
          quantity: z.number().meta({ privacy: privacy.quantity }),
        })
        .meta({ privacy: privacy.structured }),
    ),
  })
  .meta({ privacy: privacy.structured });

export interface ShopifyRefund extends z.infer<typeof RefundSchema> {}

export interface SparseShopifyRefund extends z.infer<
  typeof SparseRefundSchema
> {}

const AppSchema = z
  .object({
    id: z.string().nullish().meta({ privacy: privacy.id.external.resource }),
    name: z.string().nullish().meta({ privacy: privacy.productCatalogText }),
  })
  .meta({ privacy: privacy.structured });
const PublicationSchema = z
  .object({
    id: z.string().nullish().meta({ privacy: privacy.id.external.resource }),
    name: z.string().nullish().meta({ privacy: privacy.productCatalogText }),
  })
  .meta({ privacy: privacy.structured });

export const CancelledReasonEnum = createEnum(
  ["customer", "fraud", "inventory", "declined", "other", "staff"],
  { enumName: "CancelledReason" },
);
export const FinancialStatusEnum = createEnum(
  [
    "authorized",
    "pending",
    "paid",
    "partially_paid",
    "partially_refunded",
    "refunded",
    "voided",
  ],
  { enumName: "FinancialStatus" },
);

export const orderIdSchema_ = z.union([z.number(), z.string()]);
export const orderIdSchema: z.ZodType<OrderId> = orderIdSchema_;

/** OrderId is unique per-team AND provider (meaning not necessarily unique per team _across_ providers, so a Shopify and a Commentsold order could have the same OrderId on a given team). See ProviderPrefixedOrderId for a unique order ID per-team ACROSS providers. */
export type OrderId = z.infer<typeof orderIdSchema_>;

const OrderMetafieldSchema = z
  .object({
    namespace: z.string().meta({ privacy: privacy.resourceLabel }),
    key: z.string().meta({ privacy: privacy.resourceLabel }),
    value: z.string().meta({ privacy: privacy.untypedPayload }),
  })
  .meta({ privacy: privacy.structured });

/**
 * Schema for Shopify-shaped order data. This schema is used for both real Shopify orders
 * and synthetic orders from other platforms (CommentSold, SFOM, etc.) that are reshaped
 * to match this structure. Some fields marked optional/defaulted here are required by
 * Shopify's API but may be missing from other providers.
 */
export const ShopifyOrderSchema_ = z
  .object({
    admin_graphql_api_id: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    app_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    billing_address: CustomerAddressSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    browser_ip: z
      .string()
      .nullish()
      .meta({ privacy: privacy.ipAddress.customer }),
    buyer_accepts_marketing: z
      .boolean()
      .nullish()
      .meta({ privacy: privacy.flag }),
    cancel_reason: CancelledReasonEnum.nullish().meta({
      privacy: privacy.enumOrStatus,
    }),
    cancelled_at: iso8601DateString
      .nullish()
      .meta({ privacy: privacy.timestamp }),
    cart_token: z.string().nullish().meta({ privacy: privacy.id.session }),
    checkout_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    checkout_token: z.string().nullish().meta({ privacy: privacy.id.session }),
    client_details: ClientDetailsSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    closed_at: iso8601DateString.nullish().meta({ privacy: privacy.timestamp }),
    company: z
      .object({
        id: z
          .union([z.number(), z.string()])
          .meta({ privacy: privacy.id.external.resource }),
        location_id: z
          .union([z.number(), z.string()])
          .nullish()
          .meta({ privacy: privacy.id.external.resource }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    confirmation_number: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    confirmed: z.boolean().nullish().meta({ privacy: privacy.flag }),
    contact_email: z
      .string()
      .nullish()
      .meta({ privacy: privacy.email.customer }),
    created_at: dateOrStringOrNumberAsIso8601DateString.meta({
      privacy: privacy.timestamp,
    }),
    currency: z.string().meta({ privacy: privacy.money }),
    current_total_additional_fees_set:
      OptionalMoneySetOrStringOrArrayAsOptionalMoneySet.meta({
        privacy: privacy.structured,
      }).nullish(),
    current_total_discounts: AmountTotalOrArrayAsString.default("0"), // always provided by Shopify, defaults to "0" in Redo API
    current_total_discounts_set:
      OptionalMoneySetOrStringOrArrayAsOptionalMoneySet.meta({
        privacy: privacy.structured,
      }).nullish(),
    current_total_duties_set:
      OptionalMoneySetOrStringOrArrayAsOptionalMoneySet.meta({
        privacy: privacy.structured,
      }).nullish(),
    current_total_price: z.string().nullish().meta({ privacy: privacy.money }), // always provided by Shopify, optional in Redo API
    current_total_price_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    current_subtotal_price: z
      .string()
      .nullish()
      .meta({ privacy: privacy.money }), // always provided by Shopify, optional in Redo API
    current_subtotal_price_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    current_total_tax: z.string().nullish().meta({ privacy: privacy.money }), // always provided by Shopify, optional in Redo API
    current_total_tax_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    customer: CustomerSchema.nullish().meta({ privacy: privacy.structured }),
    customer_locale: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    device_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.device }),
    discount_applications: z
      .array(DiscountApplicationSchema)
      .default([])
      .meta({ privacy: privacy.structured }), // always provided by Shopify, defaults to [] in Redo API
    discount_codes: z
      .array(DiscountCodeSchema)
      .meta({ privacy: privacy.structured }),
    email: z.string().nullish().meta({ privacy: privacy.email.customer }),
    estimated_taxes: z.boolean().nullish().meta({ privacy: privacy.flag }),
    financial_status: FinancialStatusEnum.nullish().meta({
      privacy: privacy.enumOrStatus,
    }),
    fulfillments: z
      .array(FulfillmentSchema)
      .meta({ privacy: privacy.structured }),
    fulfillment_status: OrderFulfillmentStatusEnum.nullish().meta({
      privacy: privacy.enumOrStatus,
    }),
    gateway: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
    id: orderIdSchema.meta({ privacy: privacy.id.external.resource }),
    landing_site: z.string().nullish().meta({ privacy: privacy.publicUrl }),
    landing_site_ref: z
      .string()
      .nullish()
      .meta({ privacy: privacy.resourceLabel }),
    line_items: z.array(LineItemSchema).meta({ privacy: privacy.structured }),
    location_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    merchant_of_record_app_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    metafields: z
      .array(OrderMetafieldSchema)
      .nullish()
      .meta({ privacy: privacy.structured }),
    name: z.string().meta({ privacy: privacy.resourceLabel }),
    note: z.string().nullish().meta({ privacy: privacy.text.customer }),
    note_attributes: z
      .array(PropertySchema)
      .default([])
      .meta({ privacy: privacy.structured }), // always provided by Shopify, defaults to [] in Redo API
    number: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }), // always provided by Shopify, optional in Redo API
    order_number: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    original_total_additional_fees_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    original_total_duties_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    payment_details: PaymentDetailsSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    payment_terms: PaymentTermsSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    payment_gateway_names: z
      .array(z.string())
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    phone: z.string().nullish().meta({ privacy: privacy.phone.customer }),
    po_number: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    presentment_currency: z.string().nullish().meta({ privacy: privacy.money }),
    processing_method: z
      .string()
      .nullish()
      .meta({ privacy: privacy.enumOrStatus }),
    processed_at: dateOrStringOrNumberAsIso8601DateString.meta({
      privacy: privacy.timestamp,
    }),
    reference: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    referring_site: z.string().nullish().meta({ privacy: privacy.publicUrl }),
    refunds: z
      .union([z.array(RefundSchema), z.array(SparseRefundSchema)])
      .default([])
      .meta({ privacy: privacy.structured }), // always provided by Shopify, defaults to [] in Redo API
    shipping_address: CustomerAddressSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    shipping_lines: z
      .array(ShippingLineSchema)
      .meta({ privacy: privacy.structured }),
    source_identifier: z
      .string()
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    source_name: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
    source_url: z.string().nullish().meta({ privacy: privacy.publicUrl }),
    subtotal_price: z.string().meta({ privacy: privacy.money }),
    subtotal_price_set: z
      .object({
        shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
        presentment_money: MoneySetSchema.meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
    tags: z.string().nullish().meta({ privacy: privacy.resourceLabel }),
    tax_exempt: z.boolean().nullish().meta({ privacy: privacy.flag }),
    tax_lines: z
      .array(TaxLineSchema)
      .default([])
      .meta({ privacy: privacy.structured }), // always provided by Shopify, defaults to [] in Redo API
    taxes_included: z.boolean().default(false).meta({ privacy: privacy.flag }), // always provided by Shopify, defaults to false in Redo API
    test: z.boolean().default(false).meta({ privacy: privacy.flag }), // always provided by Shopify, defaults to false in Redo API
    token: z.string().nullish().meta({ privacy: privacy.id.session }),
    total_discounts: z.string().nullish().meta({ privacy: privacy.money }),
    total_discounts_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    total_line_items_price: stringOrNumberAsString
      .nullish()
      .meta({ privacy: privacy.money }), // always provided by Shopify, optional in Redo API
    total_line_items_price_set: z
      .object({
        shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
        presentment_money: MoneySetSchema.meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
    total_outstanding: z.string().nullish().meta({ privacy: privacy.money }),
    total_price: z.string().meta({ privacy: privacy.money }),
    total_price_usd: z.string().nullish().meta({ privacy: privacy.money }),
    total_price_set: z
      .object({
        shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
        presentment_money: MoneySetSchema.meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
    total_shipping_price_set: OptionalMoneySetSchema.meta({
      privacy: privacy.structured,
    }),
    total_tax: z.string().meta({ privacy: privacy.money }),
    total_tax_set: z
      .object({
        shop_money: MoneySetSchema.meta({ privacy: privacy.structured }),
        presentment_money: MoneySetSchema.meta({ privacy: privacy.structured }),
      })
      .meta({ privacy: privacy.structured }),
    total_tip_received: z.string().nullish().meta({ privacy: privacy.money }),
    total_weight: z.number().nullish().meta({ privacy: privacy.dimension }),
    updated_at: dateOrStringOrNumberAsIso8601DateString.meta({
      privacy: privacy.timestamp,
    }),
    user_id: z
      .union([z.number(), z.string()])
      .nullish()
      .meta({ privacy: privacy.id.external.resource }),
    order_status_url: urlSchema.nullish().meta({ privacy: privacy.privateUrl }),
    original_order: z.any().nullish().meta({ privacy: privacy.untypedPayload }),
    app: AppSchema.nullish().meta({ privacy: privacy.structured }),
    publication: PublicationSchema.nullish().meta({
      privacy: privacy.structured,
    }),
  })
  .meta({ privacy: privacy.structured });
export const ShopifyOrderSchema: z.ZodType<ShopifyOrder> = ShopifyOrderSchema_;
export interface ShopifyOrder extends z.infer<typeof ShopifyOrderSchema_> {}

/**
 * Returns the first non-null address from a Shopify order, cascading:
 * shipping_address → billing_address → customer.default_address.
 *
 * Social and accelerated checkouts (Instagram, TikTok, in-store) sometimes
 * record a null shipping_address, so this fallback chain is the canonical way
 * to resolve a shopper's address from a Shopify order.
 *
 * Accepts and returns `any` because Shopify address objects arrive in multiple
 * shapes (CustomerAddress, raw webhook payloads, Order.shopify with index
 * signature) and callers need to access non-standard fields (returnerName,
 * email, street1) that vary by context.
 */

export function getShopifyOrderFallbackAddress(shopifyOrder: any): any {
  return (
    shopifyOrder?.shipping_address ||
    shopifyOrder?.billing_address ||
    shopifyOrder?.customer?.default_address ||
    null
  );
}

/**
 * A Shopify address object does NOT carry an email. The email lives on the
 * order (`order.email` / `order.contact_email`) and the customer
 * (`order.customer.email`), so it is resolved separately from the address.
 */
export function getShopifyOrderFallbackEmail(
  shopifyOrder: any,
): string | undefined {
  return (
    shopifyOrder?.email ||
    shopifyOrder?.contact_email ||
    shopifyOrder?.customer?.email ||
    undefined
  );
}
